import React, { useState, useEffect, useRef } from 'react';
import { ChartStyle, CompleteKundali, Language, MenuLayoutMode } from '../types/kundali';
import { UserProfile } from '../lib/firebase';
import { UI_TEXT } from '../utils/language';
import { deduplicateKundalis } from '../utils/kundaliDedupe';
import { formatDOB } from '../utils/dateUtils';
import { useClickOutside } from '../hooks/useClickOutside';
import { PanchangDrawer } from './PanchangDrawer';
import { ProfileDropdown } from './ProfileDropdown';
import { SettingsModal } from './SettingsModal';
import { BtrDrawer } from './BtrDrawer';
import { UserProfileRecord, UserRole } from '../types/user';
import { APP_VERSION } from '../types';
import { getAppVersionInfo } from '../utils/versionManager';
import { AppVersionModal } from './AppVersionModal';
import { isSuperAdminUser } from '../utils/rbac';
import { SUPPORTED_LANGUAGES } from '../constants/languages';
import { LogoInfinityCursor, InfinityColorVariant } from './LogoInfinityCursor';
import {
  Sparkles,
  ScrollText,
  FileDown,
  SlidersHorizontal,
  Timer,
  Globe,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Check,
  Briefcase,
  Menu
} from 'lucide-react';

export interface HeaderProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  chartStyle?: ChartStyle;
  setChartStyle?: (style: ChartStyle) => void;
  lang?: Language;
  setLang?: (lang: Language) => void;
  viewMode?: 'simple' | 'astrologer';
  setViewMode?: (mode: 'simple' | 'astrologer') => void;
  onOpenBirthForm?: () => void;
  onNewChart?: () => void;
  kundali?: CompleteKundali | null;
  savedKundalis?: CompleteKundali[];
  onLoadSavedKundali?: (k: CompleteKundali) => void;
  onPrintReport?: () => void;
  onPrint?: () => void;
  onOpenVault?: () => void;
  onOpenSettings?: () => void;
  onOpenBTR?: () => void;
  onApplyRectifiedKundali?: (rectifiedKundali: CompleteKundali) => void;
  currentUser?: UserProfile | null;
  activeProfile?: UserProfileRecord | null;
  onSelectProfile?: (p: UserProfileRecord, k?: CompleteKundali) => void;
  onOpenAuth?: () => void;
  onOpenAdminPortal?: () => void;
  onOpenMyVault?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenBackupRestore?: () => void;
  onOpenGoldenRegression?: () => void;
  onLogout?: () => void;
  onSwitchRole?: (role: UserRole) => void;
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
  isSidebarCollapsed?: boolean;
  hideTopEssentials?: boolean;
  hideRightDock?: boolean;
  onOpenBlueprint?: () => void;
  onEditKundali?: () => void;
  onOpenVersionModal?: () => void;
  menuLayoutMode?: MenuLayoutMode;
  onMenuLayoutModeChange?: (mode: MenuLayoutMode) => void;
}

/**
 * Sacred Swastika flanked by two vertical bars (danda).
 * Calibrated so all 4 vertical bars across the logo have:
 * - Exactly 15% smaller height than the Swastika (11.2px vs 13.2px optical height)
 * - Identical stroke width (1.2px) matching the Swastika arms perfectly
 * - Zero subpixel jitter or width inconsistency across browsers and display scalings
 */
const SacredSwastikaWithBars: React.FC = () => (
  <svg
    width="22"
    height="16"
    viewBox="0 0 22 16"
    className="shrink-0 select-none overflow-visible"
    aria-label="Auspicious Swastika with Danda"
  >
    {/* Left vertical bar: exactly 15% less in height than swastika, 1.2px stroke */}
    <line
      x1="1.8"
      y1="3.0"
      x2="1.8"
      y2="13.0"
      stroke="#C82A1E"
      strokeWidth="1.2"
      strokeLinecap="round"
    />

    {/* Sacred Clockwise Swastika: 12px height, 1.2px stroke matching flanking bars */}
    <path
      d="M 17,2 L 11,2 L 11,14 L 5,14"
      fill="none"
      stroke="#C82A1E"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M 5,2 L 5,8 L 17,8 L 17,14"
      fill="none"
      stroke="#C82A1E"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    {/* Right vertical bar: exactly 15% less in height than swastika, 1.2px stroke */}
    <line
      x1="20.2"
      y1="3.0"
      x2="20.2"
      y2="13.0"
      stroke="#C82A1E"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

export const Header: React.FC<HeaderProps> = ({
  activeTab = 'analytics',
  setActiveTab,
  chartStyle = 'north',
  setChartStyle,
  lang = 'en',
  setLang,
  viewMode = 'simple',
  setViewMode,
  onOpenBirthForm,
  onNewChart,
  kundali,
  savedKundalis = [],
  onLoadSavedKundali,
  onPrintReport,
  onPrint,
  onOpenVault,
  onOpenSettings,
  onOpenBTR,
  onApplyRectifiedKundali,
  currentUser = null,
  activeProfile = null,
  onSelectProfile,
  onOpenAuth,
  onOpenAdminPortal,
  onOpenMyVault,
  onOpenWhatsAppModal,
  onOpenBackupRestore,
  onOpenGoldenRegression,
  onLogout,
  onSwitchRole,
  onToggleSidebar,
  isSidebarOpen = false,
  isSidebarCollapsed = false,
  hideTopEssentials = false,
  hideRightDock = false,
  onOpenBlueprint,
  onEditKundali,
  onOpenVersionModal,
  menuLayoutMode,
  onMenuLayoutModeChange
}) => {
  // Drawer & Popover states
  const [isPanchangOpen, setIsPanchangOpen] = useState(false);
  const [isBtrOpen, setIsBtrOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const versionInfo = getAppVersionInfo();
  const logoVariant: InfinityColorVariant = 'temple-gold';

  const isHi = lang === 'hi' || lang === 'sa' || lang === 'mr';

  // Saved Kundalis search & coords
  const [savedSearchQuery, setSavedSearchQuery] = useState('');
  const [savedDropdownCoords, setSavedDropdownCoords] = useState<{ top: number; right: number } | null>(null);

  // References for outside click dismissal
  const savedBtnRef = useRef<HTMLButtonElement | null>(null);
  const savedDropdownRef = useRef<HTMLDivElement | null>(null);
  const profileBtnRef = useRef<HTMLButtonElement | null>(null);
  const langBtnRef = useRef<HTMLButtonElement | null>(null);
  const langMenuRef = useRef<HTMLDivElement | null>(null);
  // Global Outside-Click Dismissal
  useClickOutside([savedBtnRef, savedDropdownRef], showSavedDropdown, () => setShowSavedDropdown(false));
  useClickOutside([langBtnRef, langMenuRef], isLangOpen, () => setIsLangOpen(false));

  const uniqueSavedKundalis = deduplicateKundalis(savedKundalis);
  const userSavedKundalis = uniqueSavedKundalis.filter(
    (sk) => !currentUser || (sk as any).userId === currentUser.uid || (sk as any).userEmail?.toLowerCase() === currentUser.email?.toLowerCase() || !(sk as any).userId
  );
  const filteredSavedKundalis = uniqueSavedKundalis.filter(
    (sk) =>
      (sk.birthDetails.name || '').toLowerCase().includes(savedSearchQuery.toLowerCase()) ||
      (sk.birthDetails.placeName || '').toLowerCase().includes(savedSearchQuery.toLowerCase())
  );

  const ui = UI_TEXT[lang] || UI_TEXT.en;

  const isManagementUser = Boolean(
    currentUser && (
      currentUser.role === UserRole.ADMIN ||
      currentUser.role === UserRole.SUPER_ADMIN ||
      (currentUser.role as string) === 'admin' ||
      (currentUser.role as string) === 'super_admin' ||
      currentUser.activeRole === UserRole.ADMIN ||
      currentUser.activeRole === UserRole.SUPER_ADMIN ||
      isSuperAdminUser(currentUser)
    )
  );

  // Mutual Exclusivity: Opening Panchang dismisses other drawers
  const handleTogglePanchang = () => {
    setIsPanchangOpen((prev) => {
      const next = !prev;
      if (next) {
        setIsBtrOpen(false);
        setIsLangOpen(false);
        setIsProfileOpen(false);
      }
      return next;
    });
  };

  // Mutual Exclusivity: Opening BTR dismisses other drawers
  const handleToggleBtr = () => {
    setIsBtrOpen((prev) => {
      const next = !prev;
      if (next) {
        setIsPanchangOpen(false);
        setIsLangOpen(false);
        setIsProfileOpen(false);
      }
      return next;
    });
    if (onOpenBTR) {
      onOpenBTR();
    }
  };

  // ESC key dismissal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPanchangOpen(false);
        setIsBtrOpen(false);
        setIsLangOpen(false);
        setIsProfileOpen(false);
        setShowSavedDropdown(false);
        setIsSettingsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleBrandClick = () => {
    if (setViewMode) setViewMode('simple');
    if (setActiveTab) setActiveTab('analytics');
  };

  const handleNewChartAction = () => {
    if (onNewChart) {
      onNewChart();
    } else if (onOpenBirthForm) {
      onOpenBirthForm();
    }
  };

  const handleVaultAction = () => {
    if (onOpenVault) {
      onOpenVault();
    } else if (onOpenMyVault) {
      onOpenMyVault();
    } else if (savedBtnRef.current) {
      const rect = savedBtnRef.current.getBoundingClientRect();
      setSavedDropdownCoords({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right)
      });
      setIsProfileOpen(false);
      setIsLangOpen(false);
      setShowSavedDropdown(!showSavedDropdown);
    }
  };

  const handlePrintAction = () => {
    if (onPrint) {
      onPrint();
    } else if (onPrintReport) {
      onPrintReport();
    }
  };

  const handleSettingsAction = () => {
    if (onOpenSettings) {
      onOpenSettings();
    } else {
      setIsSettingsOpen(true);
    }
  };

  // Extract user initial
  const getUserInitial = (): string => {
    if (currentUser?.displayName) {
      return currentUser.displayName.trim()[0]?.toUpperCase() || 'V';
    }
    const anyUser = currentUser as any;
    if (anyUser?.name) {
      return anyUser.name.trim()[0]?.toUpperCase() || 'V';
    }
    if (currentUser?.email) {
      return currentUser.email.trim()[0]?.toUpperCase() || 'V';
    }
    return 'B';
  };

  return (
    <>
      <header
        id="app-header-main"
        className="w-full bg-astro-bg border-b border-astro-border select-none relative z-40"
      >
        <div className="w-full px-3 sm:px-6 h-14 flex items-center justify-between gap-4 overflow-x-auto custom-horizontal-scrollbar pb-1 whitespace-nowrap">
          
          {/* ========================================================================= */}
          {/* Left Section: Sidebar Toggle & Brand (shrink-0)                           */}
          {/* ========================================================================= */}
          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
            {/* Desktop Sidebar Collapse Toggle Arrow (Visible on desktop) */}
            {onToggleSidebar && (
              <button
                type="button"
                id="sidebar-toggle-arrow"
                onClick={onToggleSidebar}
                aria-label={isSidebarCollapsed ? (isHi ? 'मेनू फैलाएं' : 'Expand Sidebar (Ctrl+B)') : (isHi ? 'मेनू सिकोड़ें' : 'Collapse Sidebar (Ctrl+B)')}
                title={isSidebarCollapsed ? (isHi ? 'मेनू फैलाएं (Ctrl+B)' : 'Expand Sidebar (Ctrl+B)') : (isHi ? 'मेनू सिकोड़ें (Ctrl+B)' : 'Collapse Sidebar (Ctrl+B)')}
                className="hidden lg:flex w-8 h-8 items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 text-[#D4AF37] border border-white/10 hover:border-[#D4AF37]/50 transition-all cursor-pointer shrink-0 shadow-xs"
              >
                {isSidebarCollapsed ? <ChevronRight className="w-4 h-4 text-[#D4AF37]" /> : <ChevronLeft className="w-4 h-4 text-[#D4AF37]" />}
              </button>
            )}

            {/* Mobile Sidebar Hamburger Trigger */}
            {onToggleSidebar && (
              <button
                type="button"
                id="header-mobile-sidebar-toggle"
                onClick={onToggleSidebar}
                aria-label={isSidebarOpen ? (isHi ? 'मेनू बंद करें' : 'Close Navigation Menu') : (isHi ? 'मेनू खोलें' : 'Open Navigation Menu')}
                title={isSidebarOpen ? (isHi ? 'मेनू बंद करें' : 'Close Navigation Menu') : (isHi ? 'मेनू खोलें' : 'Open Navigation Menu')}
                className="lg:hidden min-h-[44px] min-w-[44px] p-1.5 rounded-xl text-astro-text hover:bg-white/10 border border-astro-border/40 shrink-0 cursor-pointer flex items-center justify-center"
              >
                <Menu className="w-5 h-5 text-[#D4AF37]" />
              </button>
            )}

            {/* Application Brand */}
            <div
              id="header-brand-trigger"
              className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded-xl hover:bg-astro-card transition-colors shrink-0"
              onClick={handleBrandClick}
              title="Vedang Jyotisham — Vedic Kundali Suite"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBrandClick()}
            >
              <div className="flex items-center gap-2 shrink-0 select-none">
                {/* Capsule with Crisp Green Outline */}
                <div className="bg-[#0B3332] border-2 border-[#24827D] rounded-xl px-2.5 py-1 shadow-inner flex items-center justify-center">
                  <div className="bg-[#FFF8F6] border border-[#F5D5CE] rounded-lg px-2 py-0.5 flex items-center gap-1.5 shadow-xs">
                    <SacredSwastikaWithBars />
                    <LogoInfinityCursor width={42} height={16} variant={logoVariant} />
                    <SacredSwastikaWithBars />
                  </div>
                </div>

                {/* Brand Name */}
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-1.5">
                    <span className="font-serif font-bold text-sm tracking-wide text-astro-primary">
                      {isHi ? 'वेदांग ज्योतिषम्' : 'VEDANG JYOTISHAM'}
                    </span>
                  </div>
                  <span className="text-[10px] text-astro-muted leading-tight hidden 2xl:block">
                    Classical Vedic Jyotish Intelligence & Consultation Workspace
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ========================================================================= */}
          {/* Center Section: Daily Essentials (Panchang & Auspicious Elements)         */}
          {/* ========================================================================= */}
          <div className={`flex-1 flex items-center min-w-0 ${hideRightDock ? 'px-2 sm:px-3' : 'justify-center px-1 sm:px-2'}`}>
            {!hideTopEssentials && (
                <div className="flex items-center gap-3 shrink-0">
                  {/* Primary Top-Level: Daily Panchang Trigger */}
                  <div className="flex flex-col items-center shrink-0">
                    <button
                      id="btn-nav-panchang"
                      type="button"
                      onClick={() => {
                        if (setActiveTab) {
                          setActiveTab('panchang');
                        } else {
                          setIsPanchangOpen((prev) => !prev);
                        }
                        setIsBtrOpen(false);
                        setIsLangOpen(false);
                        setIsProfileOpen(false);
                      }}
                      className={`min-h-[44px] h-11 sm:min-h-0 sm:h-8 px-2.5 sm:px-3 inline-flex items-center gap-1.5 rounded-xl border text-xs font-semibold transition-all shadow-xs hover:scale-105 cursor-pointer select-none ${
                        activeTab === 'panchang' || activeTab === 'daily-panchang'
                          ? 'bg-[#174C4B] border-[#174C4B] text-white shadow-sm ring-1 ring-[#B58A4A]'
                          : 'bg-[#FEF3C7] border-[#FDE68A] text-[#B45309] hover:bg-[#FDE68A]'
                      }`}
                      aria-label={isHi ? 'दैनिक पंचांग' : 'Daily Panchang'}
                      title={isHi ? 'दैनिक पंचांग एवं मुहूर्त देखें' : 'View Daily Panchang & Auspicious Timings'}
                    >
                      <Calendar className={`w-3.5 h-3.5 ${
                        activeTab === 'panchang' || activeTab === 'daily-panchang' ? 'text-[#B58A4A]' : 'text-[#D97706]'
                      }`} />
                      <span className="text-[11px] font-bold">{isHi ? 'दैनिक पंचांग' : 'Daily Panchang'}</span>
                    </button>
                    <span className="hidden sm:block text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                      {isHi ? 'दैनिक काल स्थिति' : 'Daily Panchang'}
                    </span>
                  </div>

                  {/* Auspicious Elements (Shubha Essentials: Numbers, Colors, Timing & Remedies) */}
                  <div className="flex flex-col items-center shrink-0">
                    <button
                      id="btn-nav-lucky-elements"
                      type="button"
                      onClick={() => {
                        if (setActiveTab) {
                          setActiveTab('shubha-essentials');
                        }
                        window.dispatchEvent(new CustomEvent('astro_open_lucky_modal'));
                      }}
                      className={`min-h-[44px] h-11 sm:min-h-0 sm:h-8 px-2.5 sm:px-3 inline-flex items-center gap-1.5 rounded-xl border text-xs font-semibold transition-all shadow-xs hover:scale-105 cursor-pointer select-none ${
                        activeTab === 'shubha-essentials' || activeTab === 'numerology'
                          ? 'bg-[#174C4B] border-[#174C4B] text-white shadow-sm ring-1 ring-[#B58A4A]'
                          : 'bg-rose-50 border-rose-200 text-rose-850 hover:bg-rose-100 dark:bg-rose-950/40 dark:border-rose-900/60 dark:text-rose-300'
                      }`}
                      aria-label={isHi ? 'शुभ तत्व व उपाय' : 'Shubha Essentials & Remedies'}
                      title={isHi ? 'शुभ रंग, अंक, दिशा, नवतारा, मुहूर्त व उपाय' : 'View Auspicious Indicators, Muhurta & Remedies'}
                    >
                      <Sparkles className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                      <span className="text-[11px] font-bold">{isHi ? 'शुभ तत्व व उपाय' : 'Shubha Essentials'}</span>
                    </button>
                    <span className="hidden sm:block text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                      {isHi ? 'शुभ तत्व' : 'Auspicious'}
                    </span>
                  </div>
                </div>
            )}
          </div>

          {/* ========================================================================= */}
          {/* Right Section: Multi-Chromatic Dock Icons (Hidden when hideRightDock)      */}
          {/* [ ➕ ]  [ 📁 ]  [ 🖨️ ]  [ ⏱️ BTR ▾ ]  [ 🌐 Lang ▾ ]  [ ⚙ ]  |  [ (B) ▾ ]  */}
          {/* ========================================================================= */}
          {!hideRightDock && (
            <div className="flex items-center gap-1.5 shrink-0 select-none">
            
            {/* 1. New Chart (Emerald Green) */}
            <div className="flex flex-col items-center shrink-0">
              <button
                id="btn-nav-new-chart"
                type="button"
                onClick={handleNewChartAction}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#D1FAE5] hover:bg-[#A7F3D0] border border-[#A7F3D0] hover:scale-105 transition-all shadow-xs cursor-pointer"
                aria-label="New Birth Chart"
              >
                <Sparkles className="w-4 h-4 text-[#059669]" />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'नई कुंडली' : 'New Chart'}
              </span>
            </div>

            {/* 2. Kundali Vault (Warm Saffron / Orange) */}
            <div className="flex flex-col items-center shrink-0">
              <button
                id="btn-nav-vault"
                ref={savedBtnRef}
                type="button"
                onClick={handleVaultAction}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#FFEDD5] hover:bg-[#FED7AA] border border-[#FED7AA] hover:scale-105 transition-all shadow-xs cursor-pointer"
                aria-label="Kundali Vault"
              >
                <ScrollText className="w-4 h-4 text-[#D9531E]" />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'कुंडली वॉल्ट' : 'Vault'}
              </span>
            </div>

            {/* 3. Print (Royal Indigo) */}
            <div className="flex flex-col items-center shrink-0">
              <button
                id="btn-nav-print"
                type="button"
                onClick={handlePrintAction}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#EEF2FF] hover:bg-[#C7D2FE] border border-[#C7D2FE] hover:scale-105 transition-all shadow-xs cursor-pointer"
                aria-label="Print Report"
              >
                <FileDown className="w-4 h-4 text-[#4F46E5]" />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'प्रिंट रिपोर्ट' : 'Print'}
              </span>
            </div>



            {/* 4. Birth Time Rectification [ ⏱️ BTR ▾ ] (Crimson Ruby) */}
            <div className="flex flex-col items-center shrink-0">
              <button
                id="btn-nav-btr"
                type="button"
                onClick={handleToggleBtr}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl border transition-all text-xs font-semibold cursor-pointer shadow-xs hover:scale-105 ${
                  isBtrOpen
                    ? 'bg-[#DC2626] text-astro-bg border-[#DC2626]'
                    : 'bg-[#FEE2E2] hover:bg-[#FECACA] text-[#DC2626] border-[#FECACA]'
                }`}
                aria-label="Birth Time Rectification (BTR)"
              >
                <Timer className={`w-3.5 h-3.5 ${isBtrOpen ? 'text-white' : 'text-[#DC2626]'}`} />
                <span className={`text-[11px] font-bold ${isBtrOpen ? 'text-white' : 'text-[#DC2626]'}`}>BTR</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isBtrOpen ? 'rotate-180 text-white' : 'text-[#DC2626]'}`} />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'जन्म समय शुद्धि' : 'BTR'}
              </span>
            </div>

            {/* 5. Language Switcher [ 🌐 Lang ▾ ] (Sky Azure) */}
            <div className="flex flex-col items-center shrink-0 relative" ref={langMenuRef}>
              <button
                id="btn-nav-language"
                ref={langBtnRef}
                type="button"
                onClick={() => {
                  setIsLangOpen((prev) => !prev);
                  setIsPanchangOpen(false);
                  setIsBtrOpen(false);
                  setIsProfileOpen(false);
                }}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl border transition-all text-xs font-semibold cursor-pointer shadow-xs hover:scale-105 ${
                  isLangOpen
                    ? 'bg-[#0284C7] text-astro-bg border-[#0284C7]'
                    : 'bg-[#E0F2FE] hover:bg-[#BAE6FD] text-[#0284C7] border-[#BAE6FD]'
                }`}
                aria-label="Switch Language / भाषा / भाषापरिवर्तनम्"
              >
                <Globe className={`w-3.5 h-3.5 ${isLangOpen ? 'text-white' : 'text-[#0284C7]'}`} />
                <span className={`text-[10px] uppercase font-bold ${isLangOpen ? 'text-white' : 'text-[#0284C7]'}`}>{lang}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isLangOpen ? 'rotate-180 text-white' : 'text-[#0284C7]'}`} />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'भाषा' : 'Language'}
              </span>

              {/* Pan-Indic Language Selection Popover (13 Traditions) */}
              {isLangOpen && (
                <div
                  id="pan-indic-language-popover"
                  className="absolute right-0 top-full mt-2 w-64 max-h-80 overflow-y-auto bg-astro-bg border border-astro-border rounded-2xl shadow-xl py-2 z-50 animate-fade-in"
                >
                  <div className="px-3 py-1 text-[10px] uppercase font-bold tracking-wider text-astro-muted border-b border-astro-border/70 mb-1">
                    Pan-Indic Multi-Lingual Registry (13)
                  </div>
                  <div className="divide-y divide-astro-border/40">
                    {SUPPORTED_LANGUAGES.map((opt) => {
                      const isSelected = lang === opt.code;
                      return (
                        <button
                          key={opt.code}
                          type="button"
                          onClick={() => {
                            if (setLang) setLang(opt.code as Language);
                            setIsLangOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between text-xs transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-astro-card text-astro-primary font-bold'
                              : 'hover:bg-[#FAF6ED] text-astro-text'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-xs">{opt.native}</span>
                            <span className="text-[11px] text-astro-muted">({opt.label})</span>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 text-astro-accent" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* [FEATURE LOCKED: MANAGEMENT_WORKSPACE_HEADER_ICON] */}
            {isManagementUser && (
              <div className="flex flex-col items-center shrink-0">
                <button
                  id="btn-nav-management-admin"
                  type="button"
                  onClick={() => {
                    if (onOpenAdminPortal) {
                      onOpenAdminPortal();
                    }
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-100 hover:bg-amber-200 border border-amber-300 hover:scale-105 transition-all shadow-xs cursor-pointer text-amber-900"
                  aria-label="Management Administration Workspace"
                  title="Vedang Jyotisham — Practice Management & Administration CRM"
                >
                  <Briefcase className="w-4 h-4 text-amber-800" />
                </button>
                <span className="text-[9px] font-bold text-amber-900 mt-0.5 leading-none select-none">
                  {isHi ? 'प्रबंधन' : 'Management'}
                </span>
              </div>
            )}

            {/* 6. System Settings (Neutral Steel) */}
            <div className="flex flex-col items-center shrink-0">
              <button
                id="btn-global-settings"
                type="button"
                onClick={handleSettingsAction}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#F3F4F6] hover:bg-[#E5E7EB] border border-[#E5E7EB] hover:scale-105 transition-all shadow-xs cursor-pointer"
                aria-label="System Settings"
              >
                <SlidersHorizontal className="w-4 h-4 text-[#4B5563]" />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'सेटिंग्स' : 'Settings'}
              </span>
            </div>



            {/* 7. Account & Workstation Profile [ (B) ▾ ] */}
            <div className="flex flex-col items-center shrink-0 relative">
              <button
                id="btn-user-profile-menu"
                ref={profileBtnRef}
                type="button"
                onClick={() => {
                  setIsProfileOpen((prev) => !prev);
                  setIsPanchangOpen(false);
                  setIsBtrOpen(false);
                  setIsLangOpen(false);
                }}
                aria-label="Workstation Profile"
                className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-astro-card border border-astro-border hover:bg-astro-secondary-surface hover:border-astro-accent transition cursor-pointer select-none shadow-xs"
              >
                <span className="h-6 w-6 flex items-center justify-center rounded-full bg-astro-primary text-astro-bg font-semibold text-[10px]">
                  {getUserInitial()}
                </span>
                <ChevronDown className={`w-3 h-3 text-astro-accent transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
              </button>
              <span className="text-[9px] font-medium text-astro-secondary-text mt-0.5 leading-none select-none">
                {isHi ? 'प्रोफ़ाइल' : 'Profile'}
              </span>

              {/* Role-Gated Profile Dropdown Popover */}
              <ProfileDropdown
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                currentUser={currentUser}
                onLogout={onLogout || (() => {})}
                onOpenAuth={onOpenAuth}
                onSwitchRole={onSwitchRole}
                onOpenWhatsAppModal={onOpenWhatsAppModal}
                lang={lang}
              />
            </div>
          </div>
        )}

        </div>

        {/* Collapsible Panchang Utility Drawer (Mutually exclusive with BTR) */}
        {isPanchangOpen && (
          <PanchangDrawer
            onClose={() => setIsPanchangOpen(false)}
            initialCity={kundali?.birthDetails?.placeName}
            lang={lang}
          />
        )}

        {/* Dynamic BTR Calibration Drawer (Mutually exclusive with Panchang) */}
        {isBtrOpen && (
          <BtrDrawer
            isOpen={isBtrOpen}
            onClose={() => setIsBtrOpen(false)}
            kundali={kundali || undefined}
            currentUser={currentUser}
            onApplyRectifiedKundali={(rectified) => {
              if (onApplyRectifiedKundali) {
                onApplyRectifiedKundali(rectified);
              }
              setIsBtrOpen(false);
            }}
            lang={lang}
          />
        )}
      </header>

      {/* SAVED KUNDALIS POPUP */}
      {showSavedDropdown && savedDropdownCoords && (
        <div
          ref={savedDropdownRef}
          style={{
            position: 'fixed',
            top: `${savedDropdownCoords.top}px`,
            right: `${savedDropdownCoords.right}px`,
            zIndex: 60
          }}
          className="w-80 max-w-[calc(100vw-24px)] bg-astro-bg border border-astro-border rounded-2xl shadow-2xl py-2.5 animate-fade-in text-astro-text"
        >
          <div className="px-3.5 py-1.5 text-[11px] font-bold tracking-wider text-astro-primary uppercase border-b border-astro-border/70 mb-2 flex items-center justify-between">
            <span>{ui.saved} ({uniqueSavedKundalis.length})</span>
            <div className="flex items-center gap-2">
              {(onOpenVault || onOpenMyVault) && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSavedDropdown(false);
                    if (onOpenVault) onOpenVault();
                    else if (onOpenMyVault) onOpenMyVault();
                  }}
                  className="text-[11px] font-bold text-[#D9531E] hover:underline cursor-pointer flex items-center gap-1"
                >
                  <ScrollText className="w-3 h-3" />
                  <span>{isHi ? 'वॉल्ट' : 'Vault'}</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowSavedDropdown(false);
                  handleNewChartAction();
                }}
                className="text-[11px] font-bold text-astro-accent hover:underline cursor-pointer"
              >
                + {ui.createKundali}
              </button>
            </div>
          </div>

          {/* Search Input for Saved Kundalis */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-astro-secondary-text absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={isHi ? 'सभी प्रोफाइलों में कुंडलियाँ खोजें...' : 'Search Kundalis across all profiles...'}
                value={savedSearchQuery}
                onChange={(e) => setSavedSearchQuery(e.target.value)}
                className="w-full text-xs pl-8 pr-7 py-1.5 rounded-xl border border-astro-border bg-white text-astro-text focus:outline-none focus:ring-1 focus:ring-astro-primary"
              />
              {savedSearchQuery && (
                <button
                  type="button"
                  onClick={() => setSavedSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-astro-secondary-text hover:text-astro-text"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {filteredSavedKundalis.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-astro-secondary-text">
              {isHi ? 'कोई सहेजी गई कुंडली नहीं मिली' : 'No saved kundalis found'}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto divide-y divide-astro-border/40">
              {filteredSavedKundalis.map((sk, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (onLoadSavedKundali) onLoadSavedKundali(sk);
                    setShowSavedDropdown(false);
                  }}
                  className="w-full text-left px-3.5 py-2 text-xs hover:bg-astro-card text-astro-text hover:text-astro-primary transition-colors flex flex-col cursor-pointer"
                >
                  <div className="font-bold text-sm text-astro-primary truncate">{sk.birthDetails.name}</div>
                  <div className="text-[10px] text-astro-secondary-text mt-0.5 truncate">
                    {formatDOB(sk.birthDetails.date)} • {sk.houses[0]?.sign} {ui.lagna} • {sk.birthDetails.placeName}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SETTINGS MODAL */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        lang={lang}
        setLang={setLang || (() => {})}
        chartStyle={chartStyle}
        setChartStyle={setChartStyle || (() => {})}
        menuLayoutMode={menuLayoutMode}
        onMenuLayoutModeChange={onMenuLayoutModeChange}
        isSuperAdmin={isSuperAdminUser(currentUser)}
        onOpenWhatsAppModal={onOpenWhatsAppModal}
        onOpenVersionModal={onOpenVersionModal || (() => setIsVersionModalOpen(true))}
      />

      {/* APP VERSION & 6-VERSION RELEASE HISTORY MODAL */}
      <AppVersionModal
        isOpen={isVersionModalOpen}
        onClose={() => setIsVersionModalOpen(false)}
        lang={lang}
        savedKundalisCount={savedKundalis?.length || 0}
      />
    </>
  );
};

export default Header;
